import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Car,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Layers,
  LineChart,
  Package,
  ShieldCheck,
  Truck,
  UserCog,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingFooter } from "@/components/marketing/marketing-footer";
import { MarketingNavbar } from "@/components/marketing/marketing-navbar";
import { PricingCard, resolveTierCta } from "@/features/pricing/pricing-card";
import { pricingTiers } from "@/features/pricing/tiers";
import { useAuthStore } from "@/stores/auth-store";

const faqs = [
  {
    q: "Ustorio kimler için tasarlandı?",
    a: "Ustorio araç servis ve tamir atölyeleri için tasarlandı. Müşteri ve araç kayıtlarını, servis geçmişini, parça tedarikini ve mali durumu tek panelden takip etmek isteyen her atölye sahibi rahatlıkla kullanabilir.",
  },
  {
    q: "Hesap oluşturmak ne kadar sürüyor?",
    a: "Ortalama 5 dakikadan az. E-posta adresiniz ile hesap oluşturduktan sonra atölye bilgilerinizi girip ilk müşterinizi eklemeye başlayabilirsiniz.",
  },
  {
    q: "Verilerim güvende mi? Başka atölyelerle karışır mı?",
    a: "Atölyenizin tüm bilgileri size özel bir alanda tutulur. Şifreli giriş ile yalnızca siz ve eklediğiniz çalışanlarınız erişir; hiçbir şekilde başka atölyenin verisiyle karışmaz.",
  },
  {
    q: "Mobil cihazlardan kullanabilir miyim?",
    a: "Evet, Ustorio mobil uyumlu çalışır. Telefon ve tablet üzerinden masaüstü ile aynı işlevlere erişebilir, atölyenin dışında da kayıtlarınıza ulaşabilirsiniz.",
  },
  {
    q: "Çalışanlarımı sisteme ekleyebilir miyim?",
    a: "Evet. Sahip ve çalışan rolleri ayrıdır. Çalışanlar müşteri, araç ve servis kayıtlarına erişirken mali durum gibi hassas alanlar yalnızca sahibe açıktır.",
  },
  {
    q: "Mevcut müşteri ve araç kayıtlarımı aktarabilir miyim?",
    a: "Şu an manuel kayıt ekleme desteklenmektedir. Toplu içe aktarma için bizimle iletişime geçebilirsiniz; mevcut listenize göre yardımcı olmaya çalışırız.",
  },
  {
    q: "Aboneliğim biterse verilerime ne olur?",
    a: "Abonelik aktif olmadığında yeni kayıt yapılamaz, ancak mevcut verilerinize erişiminizi kaybetmezsiniz. Ödeme ile sistem yeniden aktifleşir ve kaldığınız yerden devam edersiniz.",
  },
  {
    q: "İnternet bağlantısı olmadan çalışır mı?",
    a: "Ustorio bulut tabanlı bir sistem olduğu için internet bağlantısı gereklidir. Bunun karşılığında verileriniz güvenle saklanır ve farklı cihazlardan erişebilirsiniz.",
  },
];

export function LandingPage() {
  const isAuthed = useAuthStore((s) => Boolean(s.user));

  return (
    <>
      <title>Ustorio | Atölyeniz için Dijital Servis ve Müşteri Takip Sistemi</title>
      <meta
        name="description"
        content="Ustorio ile atölyenizin müşteri, araç, servis kaydı, parça tedariki ve mali durumunu tek panelden yönetin. Plaka takibi, servis geçmişi ve daha fazlası. 5 dakikada kullanmaya başlayın."
      />
      <link rel="canonical" href="https://ustorio.com.tr/" />
    <div className="relative min-h-svh overflow-hidden bg-white text-ink-900">
      {/* Subtle dot grid, full page, soft edges */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle,rgba(15,23,42,0.04)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_60%,transparent_95%)]"
      />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-ink-900 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Ana içeriğe geç
      </a>

      <MarketingNavbar isAuthed={isAuthed} />

      <main id="main" className="relative z-10">
        <Hero isAuthed={isAuthed} />
        <TrustStrip />
        <FeatureGrid />
        <SplitFeature />
        <HowItWorks />
        <Pricing isAuthed={isAuthed} />
        <Faq />
        <FinalCta isAuthed={isAuthed} />
      </main>

      <MarketingFooter />

      <FaqJsonLd />
    </div>
    </>
  );
}

/* ----------------------------------- Hero ---------------------------------- */

function Hero({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="px-6 pb-12 pt-12 sm:pt-20 lg:px-12 lg:pb-20 lg:pt-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16">
        <div>
          <h1 className="text-balance text-4xl font-bold leading-[1.05] tracking-[-0.02em] text-ink-900 sm:text-5xl lg:text-6xl">
            Defter karıştırmaya son.{" "}
            <span className="bg-gradient-to-r from-brand-500 via-brand-600 to-brand-800 bg-clip-text text-transparent">
              Atölyenin dijital arşivi.
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-balance text-base leading-relaxed text-ink-600 sm:text-lg">
            Müşteri, araç, servis kaydı, parça tedariki ve mali durum. Hepsi
            tek panelde. Hangi araca ne zaman ne yapıldığını saniyeler içinde
            bul.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            {isAuthed ? (
              <Button asChild size="lg" className="gap-2">
                <Link to="/dashboard">
                  Uygulamaya Git
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" className="gap-2">
                  <Link to="/register">
                    Ücretsiz hesap oluştur
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/login">Giriş yap</Link>
                </Button>
              </>
            )}
          </div>

          <ul className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-500">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-brand-600" />
              5 dakikada kurulum
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-brand-600" />
              Türkçe arayüz
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-brand-600" />
              Mobil uyumlu
            </li>
          </ul>
        </div>

        <ProductMockup />
      </div>
    </section>
  );
}

/* ------------------------------ Product Mockup ----------------------------- */

function ProductMockup() {
  return (
    <div className="relative">
      {/* Glow behind mockup */}
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-brand-200/40 via-orange-200/30 to-transparent blur-2xl"
      />

      <div className="relative overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-2xl shadow-ink-900/10 ring-1 ring-ink-900/5">
        {/* Window chrome */}
        <div className="flex items-center gap-2 border-b border-ink-200/70 bg-ink-50 px-4 py-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-ink-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-ink-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-ink-300" />
          </div>
          <div className="ml-3 flex h-6 flex-1 items-center justify-center rounded-md bg-white px-3 text-[11px] text-ink-400 ring-1 ring-ink-200">
            ustorio.com.tr/dashboard
          </div>
        </div>

        {/* App body */}
        <div className="grid grid-cols-[140px_1fr]">
          {/* Sidebar */}
          <div className="space-y-1 border-r border-ink-200/70 bg-white p-3">
            <div className="mb-2 px-2 text-[9px] font-semibold uppercase tracking-wider text-ink-400">
              Genel
            </div>
            <MockNavItem icon={Layers} label="Gösterge" active />
            <MockNavItem icon={Users} label="Müşteriler" />
            <MockNavItem icon={Car} label="Araçlar" />
            <MockNavItem icon={ClipboardList} label="Servis" />
            <MockNavItem icon={Package} label="Parça" />
            <MockNavItem icon={Truck} label="Tedarikçi" />
            <div className="mb-2 mt-3 border-t border-ink-200/70 pt-3 px-2 text-[9px] font-semibold uppercase tracking-wider text-ink-400">
              Yönetim
            </div>
            <MockNavItem icon={Wallet} label="Mali durum" />
            <MockNavItem icon={UserCog} label="Çalışanlar" />
          </div>

          {/* Main */}
          <div className="space-y-3 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-ink-500">
                  Bugün
                </div>
                <div className="text-sm font-semibold text-ink-900">
                  Servis Kayıtları
                </div>
              </div>
              <div className="rounded-md bg-brand-500 px-2.5 py-1 text-[10px] font-medium text-white shadow-sm">
                + Yeni kayıt
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <MockStat label="Aktif servis" value="12" />
              <MockStat label="Tamamlanan" value="48" tone="brand" />
              <MockStat label="Beklemede" value="3" />
            </div>

            <div className="space-y-1.5 rounded-lg border border-ink-200/70 bg-white p-2">
              <MockServiceRow
                plate="34 ABC 123"
                customer="Ahmet Yılmaz"
                status="Devam ediyor"
                statusTone="brand"
              />
              <MockServiceRow
                plate="06 XYZ 045"
                customer="Mehmet Demir"
                status="Tamamlandı"
                statusTone="green"
              />
              <MockServiceRow
                plate="35 DEF 789"
                customer="Ayşe Kaya"
                status="Parça bekleniyor"
                statusTone="amber"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockNavItem({
  icon: Icon,
  label,
  active = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
}) {
  return (
    <div
      className={
        "flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] " +
        (active
          ? "bg-brand-50 font-medium text-brand-700"
          : "text-ink-600")
      }
    >
      <Icon
        className={
          "h-3.5 w-3.5 " + (active ? "text-brand-600" : "text-ink-400")
        }
      />
      {label}
    </div>
  );
}

function MockStat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "brand";
}) {
  return (
    <div
      className={
        "rounded-md border p-2 " +
        (tone === "brand"
          ? "border-brand-200 bg-brand-50"
          : "border-ink-200/70 bg-white")
      }
    >
      <div className="text-[9px] font-medium text-ink-500">{label}</div>
      <div
        className={
          "mt-0.5 text-base font-bold " +
          (tone === "brand" ? "text-brand-700" : "text-ink-900")
        }
      >
        {value}
      </div>
    </div>
  );
}

function MockServiceRow({
  plate,
  customer,
  status,
  statusTone,
}: {
  plate: string;
  customer: string;
  status: string;
  statusTone: "brand" | "green" | "amber";
}) {
  const tone = {
    brand: "bg-brand-50 text-brand-700 ring-brand-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
  }[statusTone];

  return (
    <div className="flex items-center gap-2 rounded-md px-1.5 py-1">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ink-100 text-ink-500">
        <Car className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[11px] font-semibold text-ink-900">
          {plate}
        </div>
        <div className="truncate text-[10px] text-ink-500">{customer}</div>
      </div>
      <div
        className={
          "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ring-1 " +
          tone
        }
      >
        {status}
      </div>
    </div>
  );
}

/* -------------------------------- Trust strip ------------------------------ */

function TrustStrip() {
  const items = [
    "Plaka & şasi takibi",
    "Servis geçmişi",
    "Parça maliyeti",
    "Mali durum",
    "Çalışan rolleri",
    "Tedarikçi kartı",
  ];
  return (
    <section className="border-y border-ink-200/60 bg-white/60 px-6 py-6 backdrop-blur lg:px-12">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-medium uppercase tracking-wider text-ink-500">
        {items.map((it, i) => (
          <span key={it} className="flex items-center gap-x-6">
            {i > 0 && (
              <span
                aria-hidden
                className="h-1 w-1 rounded-full bg-ink-300"
              />
            )}
            {it}
          </span>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------- Feature grid ------------------------------ */

function FeatureGrid() {
  const features = [
    {
      icon: Users,
      title: "Müşteri & Araç kartları",
      desc: "Plaka, şasi, motor no ve kilometre. Her aracın geçmişi tek tıkla önünde.",
    },
    {
      icon: ClipboardList,
      title: "Servis geçmişi",
      desc: "Tarih, maliyet, açıklama. Gelen her araç için tüm işçilik adımları kayıt altında.",
    },
    {
      icon: Package,
      title: "Parça tedarik takibi",
      desc: "Hangi parça, hangi araca, hangi tedarikçiden, kaça alındı. Kayıp yok.",
    },
    {
      icon: Wallet,
      title: "Mali durum",
      desc: "Kazanç, gider ve net durum tek bakışta. Sahip rolüne özel detaylı görünüm.",
    },
    {
      icon: UserCog,
      title: "Çalışan rolleri",
      desc: "Sahip ve çalışan rolleri ayrı yetkilerle. Hassas verilere erişim kontrol altında.",
    },
    {
      icon: ShieldCheck,
      title: "Güvenli erişim",
      desc: "Atölyenin bilgileri sana özel. Şifreli giriş ile sadece sen ve çalışanların ulaşır, kimseyle karışmaz.",
    },
  ];

  return (
    <section
      id="ozellikler"
      className="px-6 py-20 lg:px-12 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Özellikler
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl lg:text-5xl">
            Atölyeyi yönetmek için ihtiyacın olan her şey
          </h2>
          <p className="mt-4 text-balance text-base leading-relaxed text-ink-600">
            Tek panelden müşteriden tedarikçiye, servis kaydından mali duruma.
            Atölyenin tüm operasyonu.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              className="group relative overflow-hidden rounded-2xl border border-ink-200/70 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-900/5"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-brand-100/0 blur-2xl transition-colors group-hover:bg-brand-100/60"
              />
              <div className="relative">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-ink-200/70 bg-gradient-to-br from-white to-ink-50 text-brand-600 shadow-sm transition-colors group-hover:border-brand-200 group-hover:from-brand-50 group-hover:text-brand-700">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-ink-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-500">
                  {f.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Split feature ----------------------------- */

function SplitFeature() {
  return (
    <section className="px-6 py-20 lg:px-12 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="order-2 lg:order-1">
            <div className="rounded-2xl border border-ink-200/70 bg-white p-6 shadow-xl shadow-ink-900/5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-medium text-ink-500">
                    34 ABC 123 · Ford Focus
                  </div>
                  <div className="text-base font-semibold text-ink-900">
                    Servis geçmişi
                  </div>
                </div>
                <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                  Aktif
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  {
                    date: "12 Mart 2026",
                    title: "Yağ ve filtre değişimi",
                    cost: "₺1.850",
                    note: "Sentetik 5W-30 · 5L",
                  },
                  {
                    date: "08 Şub 2026",
                    title: "Ön fren balatası",
                    cost: "₺2.400",
                    note: "Brembo · garanti dahil",
                  },
                  {
                    date: "21 Oca 2026",
                    title: "Periyodik bakım",
                    cost: "₺3.120",
                    note: "82.500 km · genel kontrol",
                  },
                ].map((row) => (
                  <li
                    key={row.title}
                    className="flex items-start gap-3 rounded-xl border border-ink-200/70 bg-white p-3"
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                      <Wrench className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="truncate text-sm font-semibold text-ink-900">
                          {row.title}
                        </div>
                        <div className="shrink-0 text-sm font-semibold tabular-nums text-ink-900">
                          {row.cost}
                        </div>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                        <Calendar className="h-3 w-3" />
                        {row.date}
                        <span className="h-1 w-1 rounded-full bg-ink-300" />
                        {row.note}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
              Servis geçmişi
            </div>
            <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl">
              Aracın hikayesi tek bir kartta.
            </h2>
            <p className="mt-4 text-balance text-base leading-relaxed text-ink-600">
              Bir araca ne zaman, ne yapıldı, ne kadar tutar tutuldu, hangi
              parça takıldı. Hepsini kronolojik olarak gör. Müşteri "geçen
              defa ne olmuştu?" dediğinde tek tıkla cevap.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Plaka, şasi, motor no, kilometre",
                "Tarih bazlı kronolojik kayıtlar",
                "Maliyet ve parça eşleştirmesi",
                "Dosya ekleri ve özel notlar",
              ].map((it) => (
                <li
                  key={it}
                  className="flex items-center gap-3 text-sm text-ink-700"
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </div>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ How it works ------------------------------- */

function HowItWorks() {
  const steps = [
    {
      icon: UserCog,
      title: "Hesabını oluştur",
      desc: "Atölyeni birkaç dakikada kaydet, çalışanlarını davet et. Her atölyenin verisi kendi hesabında.",
    },
    {
      icon: Users,
      title: "Müşteri ve araçları ekle",
      desc: "Mevcut müşterilerini ve araçlarını sisteme aktar. Plaka ile arama saniyeler sürer.",
    },
    {
      icon: LineChart,
      title: "Kayıt tut, raporla",
      desc: "Her servis ve parça hareketini kaydet. Mali durumu ve performansı tek panelden takip et.",
    },
  ];

  return (
    <section
      id="nasil-calisir"
      className="bg-ink-50/50 px-6 py-20 lg:px-12 lg:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Nasıl çalışır
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl lg:text-5xl">
            Üç adımda kullanmaya başla
          </h2>
        </div>

        <div className="relative mt-14 grid gap-8 md:grid-cols-3">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-12 hidden h-px bg-gradient-to-r from-transparent via-ink-200 to-transparent md:block"
          />
          {steps.map((s, i) => (
            <div key={s.title} className="relative text-center">
              <div className="relative z-10 mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-ink-200/70 bg-white text-brand-600 shadow-sm">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-ink-400">
                Adım {i + 1}
              </div>
              <h3 className="mt-1 text-lg font-semibold text-ink-900">
                {s.title}
              </h3>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-ink-500">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- Pricing --------------------------------- */

function Pricing({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section id="fiyatlandirma" className="px-6 py-20 lg:px-12 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Fiyatlandırma
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl lg:text-5xl">
            Atölyenizin ölçeğine uygun paket
          </h2>
          <p className="mt-4 text-balance text-base leading-relaxed text-ink-600 sm:text-lg">
            Küçük bir atölyeden çok şubeli işletmeye kadar, ihtiyacınıza göre
            ölçeklenen paketler. Tüm paketlerde ilk 14 gün ücretsiz.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {pricingTiers.map((tier) => {
            const cta = resolveTierCta(tier, isAuthed);
            return (
              <PricingCard
                key={tier.key}
                tier={tier}
                href={cta.href}
                label={cta.label}
              />
            );
          })}
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 text-center">
          <Button asChild size="lg" variant="outline" className="gap-2">
            <Link to="/fiyatlandirma">
              Detaylı fiyatlandırmayı incele
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <p className="text-sm text-ink-500">
            Karşılaştırma tablosu, sıkça sorulan sorular ve daha fazlası için
            fiyatlandırma sayfasını ziyaret edin.
          </p>
        </div>
      </div>
    </section>
  );
}


/* --------------------------------- Final CTA ------------------------------- */

function FinalCta({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="px-6 py-20 lg:px-12 lg:py-28">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-ink-200/70 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 p-10 text-center shadow-2xl shadow-ink-900/20 sm:p-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 -bottom-32 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]"
        />

        <div className="relative">
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
            Atölyeni bugün dijitalleştir.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-balance text-base leading-relaxed text-ink-300">
            Birkaç dakikada hesap oluştur, ilk müşterini ekle ve servis
            geçmişini biriktirmeye başla.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {isAuthed ? (
              <Button asChild size="lg" className="gap-2">
                <Link to="/dashboard">
                  Uygulamaya Git
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" className="gap-2">
                  <Link to="/register">
                    Ücretsiz hesap oluştur
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/login">Giriş yap</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- FAQ ---------------------------------- */

function Faq() {
  return (
    <section id="sss" className="px-6 py-20 lg:px-12 lg:py-28">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Sıkça Sorulan Sorular
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl lg:text-5xl">
            Aklındaki soruların cevabı burada
          </h2>
          <p className="mt-4 text-balance text-base leading-relaxed text-ink-600">
            Ustorio hakkında en çok sorulanları derledik. Aradığını bulamazsan
            iletişime geçebilirsin.
          </p>
        </div>

        <div className="mt-12 space-y-3">
          {faqs.map((item) => (
            <details
              key={item.q}
              className="group rounded-2xl border border-ink-200/70 bg-white p-5 transition-all open:shadow-md open:shadow-ink-900/5 hover:border-brand-200"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                <h3 className="text-base font-semibold text-ink-900 sm:text-lg">
                  {item.q}
                </h3>
                <ChevronDown className="h-5 w-5 shrink-0 text-ink-400 transition-transform duration-200 group-open:rotate-180 group-open:text-brand-600" />
              </summary>
              <p className="mt-4 text-sm leading-relaxed text-ink-600 sm:text-base">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

